import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import { updateServerBilling } from "src/actions/mine_craft/serversActions";
import { formattedDate } from "src/helpers/DateHelpers";
import { useAppDispatch } from "src/store/hooks";

import { Button, DatePicker, InputNumber, Modal, Switch, message } from 'antd';

// Comps and corrections only: nothing here moves coins. The owner pays
// from the app, and the rent sweep renews from their c_coins when
// auto-renew is on.
const _RentForm = ({ server, open, onClose }: {
  server: ServerInterface,
  open: boolean,
  onClose: () => void
}) => {
  const dispatch = useAppDispatch();
  const hosted = server.hosted_server;

  const [billed, setBilled] = useState(false);
  const [paidUntil, setPaidUntil] = useState<Moment | null>(null);
  const [autoRenew, setAutoRenew] = useState(true);
  const [rentCoins, setRentCoins] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !hosted) return;

    setBilled(!!hosted.paid_until);
    setPaidUntil(hosted.paid_until ? moment(hosted.paid_until) : null);
    setAutoRenew(hosted.auto_renew);
    setRentCoins(hosted.rent_coins);
  }, [open, hosted]);

  if (!hosted) return null;

  // The API refuses rent edits on a server archived, or being archived.
  const archived = hosted.state === 'archived' || !!hosted.archive_requested_at;

  function addMonth() {
    const base = paidUntil && paidUntil.isAfter(moment()) ? paidUntil : moment();
    setPaidUntil(base.clone().add(1, 'month'));
  }

  async function apply() {
    if (billed && !paidUntil) {
      message.error('Pick the date the rent is paid until');
      return;
    }

    const data = billed
      ? { paid_until: paidUntil!.toISOString(), auto_renew: autoRenew, rent_coins: rentCoins }
      : { paid_until: null };

    setBusy(true);
    const resultAction = await dispatch(updateServerBilling({ id: server.id, data }));
    setBusy(false);

    if (updateServerBilling.fulfilled.match(resultAction)) {
      message.success(billed ? 'Rent saved' : 'Billing stopped');
      onClose();
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload) ? payload[0] : 'The rent could not be saved');
    }
  }

  return (
    <Modal
      title='Rent'
      open={open}
      onCancel={onClose}
      onOk={apply}
      okText='Save'
      okButtonProps={{ loading: busy, disabled: archived }}
      destroyOnClose
    >
      <div className='v-srv-power'>
        <div className='o-field'>
          <label>Now</label>
          <div>{rentSummary(server)}</div>
        </div>

        {archived &&
          <div className='o-hint'>
            An archived server only has its backup left: its rent cannot be edited here.
          </div>
        }

        <div className='o-field'>
          <label>Billed</label>
          <Switch checked={billed} onChange={setBilled} disabled={archived} />
        </div>

        {billed &&
          <>
            <div className='o-field'>
              <label>Paid until</label>
              <div className='o-value-row'>
                <DatePicker showTime value={paidUntil} onChange={setPaidUntil} allowClear={false} />
                <Button onClick={addMonth}>+ 1 month</Button>
              </div>
            </div>

            <div className='o-field'>
              <label>Rent (c_coins a month)</label>
              <InputNumber min={0} value={rentCoins} onChange={setRentCoins} placeholder='the plan rent' />
            </div>

            <div className='o-field'>
              <label>Auto-renew from the owner's c_coins</label>
              <Switch checked={autoRenew} onChange={setAutoRenew} />
            </div>
          </>
        }

        <div className='o-hint'>
          No coins move here. The paid-until date you save becomes the day of the month the rent
          falls due on from then on. A date in the future lifts a suspension and starts the server
          again; turning billing off does too, and the server is then never charged or deleted for
          rent. Left empty, the rent defaults to the plan's.
        </div>
      </div>
    </Modal>
  );
}

export function rentSummary(server: ServerInterface | undefined) {
  const hosted = server?.hosted_server;

  if (server?.kind === 'official') return 'Not billed (official servers never are)';
  if (!hosted?.paid_until) return 'Not billed';

  if (hosted.state === 'archived' && hosted.deletes_at) {
    return `Archived ${hosted.archived_at ? formattedDate(hosted.archived_at).fromNow : ''}: the backup and the server are deleted on ${formattedDate(hosted.deletes_at).date}`;
  }

  if (hosted.archive_requested_at) {
    return 'Being archived: the server is stopped and its backup saved before it is removed';
  }

  if (hosted.grace_ends_at) {
    return `Ran out ${formattedDate(hosted.paid_until).fromNow}: the server runs until ${formattedDate(hosted.grace_ends_at).dateTime}, then it is archived`;
  }

  const until = formattedDate(hosted.paid_until);
  const lapsed = new Date(hosted.paid_until) <= new Date();

  return `${lapsed ? 'Ran out' : 'Paid until'} ${until.dateTime} (${until.fromNow})`;
}

export default _RentForm;
